import * as React from "react";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { api } from "../api";
import { CompoJSON } from "../models/compos";
import { Feed, FeedItemUpcomingCompo } from "../../../server/feed/buildfeed";
import { RegisterPage } from "../Auth/RegisterPage";
import { Util } from "../util";
import { FeedView } from "../Feed/FeedView";
import { TwoColumnView } from "../DesignSystem/formatting/TwoColumnView";
import { OngoingContests } from "../Feed/OngoingContests";
import { TopRecentTracks } from "../Feed/TopRecentTracks";

const inspirationalMessages = [
  {
    message: "Chips made me start making music again.",
    user: "NahuPyrope",
  },
  {
    message: "I’ve grown so much in this short span of time",
    user: "DubWolfer",
  },
  {
    message: "Don't have time for Chips Compo? Make time for Chips Compo.",
    user: "MegaSphere",
  },
  {
    message:
      "I probably wouldn't have won [the WipEout Omega Collection competition] if it wasn't for the music making habit I gained with chips",
    user: "DemonWolf",
  },
];

export const HomePage = (): JSX.Element => {
  const [indexData, setIndexData] = useState<Feed>();

  const currentInspirationalMessage = useMemo(
    () =>
      inspirationalMessages[
        Math.floor(Math.random() * inspirationalMessages.length)
      ],
    []
  );

  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      const fetchedIndex = await api.get("/api/index");
      if (!isMounted) {
        return;
      }
      setIndexData(fetchedIndex);
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, []);

  if (!indexData) {
    return <div>Loading!</div>;
  }

  const upcomingContests = indexData.filter(
    (c) => c.type === "upcoming-contest"
  ) as FeedItemUpcomingCompo[];

  const sortedContests = Util.SortByKey(upcomingContests, (c) =>
    new Date(c.compo.stop).getTime()
  );

  let latestCompo: CompoJSON | undefined;

  for (const compo of sortedContests) {
    if (new Date(compo.compo.stop) > new Date()) {
      latestCompo = compo.compo;
    }
  }

  const topOfMonth = indexData.find((x) => x.type === "topofmonth");
  const events = indexData.filter((x) => x.type === "event");
  const compos = indexData.filter((x) => x.type === "upcoming-contest");
  const feedItems = indexData.filter(
    (x) => x.type !== "topofmonth" && x.type !== "event"
  );

  return (
    <div>
      <div className="mx-auto flex max-w-5xl flex-col gap-12 px-6 py-16 lg:flex-row lg:items-start lg:gap-12 lg:py-24">
        <div className="flex-1">
          <h1 className="text-4xl font-bold leading-tight text-gray-900 dark:text-white lg:text-5xl">
            Chips Compo
          </h1>
          <p className="mt-4 text-xl leading-relaxed text-gray-600 dark:text-gray-300 lg:text-2xl">
            A music community centered around friendly music competitions
            (compos). We care a lot about giving and getting good feedback.
          </p>

          <div className="mt-10 rounded-xl border-l-4 border-amber-400 bg-gray-50 p-6 dark:bg-gray-800/50">
            <p className="text-lg italic text-gray-700 dark:text-gray-300">
              "{currentInspirationalMessage.message}"
            </p>
            <p className="mt-3 font-medium text-amber-600 dark:text-amber-400">
              — {currentInspirationalMessage.user}
            </p>
          </div>

          {latestCompo && (
            <Link
              to={`/compo/${latestCompo.id}`}
              className="mt-8 flex items-center gap-3 text-gray-600 transition-colors hover:text-amber-600 dark:text-gray-400 dark:hover:text-amber-400"
            >
              <i className="fa fa-music text-amber-500"></i>
              <span>
                Current compo:{" "}
                <span className="font-semibold text-gray-900 dark:text-white">
                  {latestCompo.name}
                </span>
              </span>
            </Link>
          )}
        </div>

        <div className="w-full lg:w-96">
          <RegisterPage />
        </div>
      </div>

      <div className="bg-slate-100 dark:bg-slate-700 py-4">
        <h2 className="mx-auto px-1 text-2xl font-semibold text-gray-900 dark:text-white sm:px-10 md:px-20 lg:px-32 xl:px-[200px]">
          Recent Chips Activity
        </h2>
      </div>

      <div className="bg-slate-100 text-slate-900 dark:bg-slate-900 dark:text-white">
        <div className="mx-auto px-1 py-8 sm:px-10 md:px-20 lg:px-32 xl:px-[200px]">
          <TwoColumnView
            left={<FeedView items={feedItems} hideSubmitButton />}
            rightTop={
              <OngoingContests
                title="Upcoming"
                contestsJSON={[...compos, ...events]}
              />
            }
            rightBottom={
              topOfMonth ? <TopRecentTracks topOfMonth={topOfMonth} /> : null
            }
          />
        </div>
      </div>
    </div>
  );
};
